/** Purchase modal — prototype visuals, real tenders.
 * Select a method (incl. Apple Pay / Google Pay), then Confirm & pay.
 * Card → checkout (reserves) → Payment Element.
 * Wallets → Express Checkout (selected wallet only); reserve only on wallet confirm. */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElement,
  StripePaymentElement,
} from '@stripe/stripe-js';
import * as api from '../api';
import { getStripe, stripeConfigured } from '../stripe';
import { BeatDetail, CheckoutResult, Currency, LicenseVersion, fmtCents, fmtPoints } from '../types';
import { BLUE, BsPortal, BsThemeContext, DiscIcon, s } from '../ui';

interface Props {
  beat: BeatDetail;
  currency: Currency;
  pointsBalance: number | null;
  onClose: () => void;
  onPurchased: (result: CheckoutResult) => void;
}

type Tender = 'points' | 'nova_card' | 'earnings_offset' | 'card_on_file' | 'apple_pay' | 'google_pay';
type PendingKind = 'card' | 'wallet';

const TERMINAL_FAIL = new Set(['failed', 'expired', 'reversed']);
const WALLET_TENDERS = new Set<Tender>(['apple_pay', 'google_pay']);

function checkoutErrorMessage(e: any): string {
  if (e?.code === 'INSUFFICIENT_POINTS') return 'Not enough Nova Points for this purchase';
  if (e?.code === 'POINTS_UNAVAILABLE') return 'Nova Points purchases are coming soon';
  if (e?.code === 'NOVA_CARD_UNAVAILABLE') return 'Nova Account purchases are coming soon';
  if (e?.code === 'BEAT_HELD_BY_YOU') {
    return 'You already have a payment in progress for this beat — finish it or go Back to cancel and try again';
  }
  if (e?.code === 'BEAT_JUST_SOLD' || e?.code === 'BEAT_NOT_AVAILABLE') {
    return 'Someone beat you to it — this beat was just purchased';
  }
  if (e?.code === 'PSP_ERROR') return 'Payment could not be started — please try again';
  return e?.message || 'Purchase failed';
}

export const PurchaseModal: React.FC<Props> = ({ beat, currency, pointsBalance, onClose, onPurchased }) => {
  const [license, setLicense] = useState<LicenseVersion | null>(null);
  const [payment, setPayment] = useState<Tender>('card_on_file');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingIntent, setPendingIntent] = useState<CheckoutResult | null>(null);
  const [pendingKind, setPendingKind] = useState<PendingKind | null>(null);
  const theme = useContext(BsThemeContext);
  const [payReady, setPayReady] = useState(false);
  const [expressReady, setExpressReady] = useState(false);
  const payElementRef = useRef<HTMLDivElement | null>(null);
  const expressElementRef = useRef<HTMLDivElement | null>(null);
  const stripeCardRef = useRef<{ stripe: Stripe; elements: StripeElements; paymentElement: StripePaymentElement } | null>(null);
  const stripeWalletRef = useRef<{ stripe: Stripe; elements: StripeElements; expressElement: StripeExpressCheckoutElement } | null>(null);
  const pendingIntentRef = useRef<CheckoutResult | null>(null);
  const licenseRef = useRef<LicenseVersion | null>(null);
  const paymentRef = useRef<Tender>(payment);

  useEffect(() => { pendingIntentRef.current = pendingIntent; }, [pendingIntent]);
  useEffect(() => { licenseRef.current = license; }, [license]);
  useEffect(() => { paymentRef.current = payment; }, [payment]);

  useEffect(() => { api.getCurrentLicense('buyer').then(setLicense).catch(() => setError('Could not load license terms')); }, []);

  useEffect(() => {
    setPayment('card_on_file');
  }, [currency]);

  const methods: { id: Tender; label: string; sub: string; disabled?: boolean }[] = [
    { id: 'points', label: 'Nova Points', sub: 'Coming soon', disabled: true },
    { id: 'nova_card', label: 'Nova Account', sub: 'Coming soon', disabled: true },
    { id: 'earnings_offset', label: 'Early pay day', sub: 'Offset against accrued earnings — coming soon', disabled: true },
    { id: 'card_on_file', label: 'Card', sub: 'Pay securely with card (USD)' },
  ];

  const priceText = currency === 'points' ? fmtPoints(beat.price_points) : fmtCents(beat.price_cents);
  const confirmLabel = payment === 'points'
    ? `Confirm & pay ${fmtPoints(beat.price_points)}`
    : payment === 'apple_pay'
      ? `Confirm & pay with Apple Pay`
      : payment === 'google_pay'
        ? `Confirm & pay with Google Pay`
        : `Confirm & pay ${fmtCents(beat.price_cents)}`;
  const inPaymentStep = pendingKind === 'wallet' || !!pendingIntent;

  const paymentReturnUrl = useCallback((orderId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('beatshop_order', orderId);
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('redirect_status');
    return url.toString();
  }, []);

  const pollUntilPaid = useCallback(async (intent: CheckoutResult) => {
    for (let i = 0; i < 30; i++) {
      const order = await api.getOrder(intent.orderId);
      if (order.status === 'paid') {
        onPurchased({ ...intent, status: 'paid', entitlementId: order.entitlement_id });
        return;
      }
      if (TERMINAL_FAIL.has(order.status)) {
        setError(order.status === 'expired'
          ? 'Payment window expired — the beat was released. Please try again.'
          : 'Payment failed — the beat was released. Please try again.');
        setPendingIntent(null);
        setPendingKind(null);
        return;
      }
      await new Promise((r) => setTimeout(r, 700));
    }
    setError('Payment is processing — open Vault in a moment to confirm your purchase');
  }, [onPurchased]);

  const runCheckout = async (tender: Tender): Promise<CheckoutResult | null> => {
    const lic = licenseRef.current;
    if (!lic) return null;
    const idemKey = `ui-${beat.id}-${tender}-${Date.now()}`;
    return api.checkout(
      { beat_id: beat.id, tender, license_version_id: lic.id, agreed: true },
      idemKey,
    );
  };

  const releasePendingIfAny = async () => {
    const intent = pendingIntentRef.current;
    if (!intent?.orderId) return;
    try {
      await api.cancelPendingOrder(intent.orderId);
    } catch {
      /* best-effort; sweeper still expires holds */
    }
    pendingIntentRef.current = null;
  };

  const resetPaymentStep = () => {
    setPendingIntent(null);
    setPendingKind(null);
    setPayReady(false);
    setExpressReady(false);
  };

  const backToMethods = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await releasePendingIfAny();
      resetPaymentStep();
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await releasePendingIfAny();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  /** Confirm & pay — card opens Payment Element; wallets open Express Checkout. */
  const onConfirm = async () => {
    if (!agreed || !license || busy) return;
    setError(null);

    if (payment === 'points') {
      setError('Nova Points purchases are coming soon');
      return;
    }
    if (payment === 'nova_card') {
      setError('Nova Account purchases are coming soon');
      return;
    }

    if (WALLET_TENDERS.has(payment)) {
      if (!stripeConfigured()) {
        setError('Payments are not configured (missing Stripe publishable key).');
        return;
      }
      setPendingIntent(null);
      setPendingKind('wallet');
      return;
    }

    setBusy(true);
    try {
      const result = await runCheckout(payment);
      if (!result) return;
      if (result.status === 'paid') { onPurchased(result); return; }
      if (result.status === 'pending' && result.clientSecret) {
        if (!api.isMockPayments(result.clientSecret) && !stripeConfigured()) {
          setError('Payments are not configured (missing Stripe publishable key).');
          await api.cancelPendingOrder(result.orderId).catch(() => {});
          return;
        }
        setPendingKind('card');
        setPendingIntent(result);
        return;
      }
      if (result.status === 'pending' && result.idempotent) {
        setError('Payment already started — refresh and check your Vault, or retry in a moment');
        return;
      }
      setError('Unexpected checkout state — please retry');
    } catch (e: any) {
      setError(checkoutErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Card path — Payment Element only (wallets disabled here). */
  useEffect(() => {
    if (!pendingIntent?.clientSecret || pendingKind !== 'card') return undefined;
    if (api.isMockPayments(pendingIntent.clientSecret)) return undefined;
    let cancelled = false;
    let paymentElement: StripePaymentElement | null = null;
    (async () => {
      const stripe = await getStripe();
      if (cancelled) return;
      if (!stripe) {
        setError('Payments are not configured (missing Stripe publishable key)');
        return;
      }
      const elements = stripe.elements({
        clientSecret: pendingIntent.clientSecret!,
        appearance: {
          theme: theme === 'dark' ? 'night' : 'stripe',
          variables: { colorPrimary: BLUE, borderRadius: '10px', fontFamily: "'Inter', system-ui, sans-serif" },
        },
      });
      const created = elements.create('payment', {
        layout: 'tabs',
        wallets: { applePay: 'never', googlePay: 'never' },
      });
      paymentElement = created;
      if (cancelled || !payElementRef.current) return;
      created.mount(payElementRef.current);
      created.on('ready', () => { if (!cancelled) setPayReady(true); });
      stripeCardRef.current = { stripe, elements, paymentElement: created };
    })();
    return () => {
      cancelled = true;
      try { paymentElement?.unmount(); } catch { /* already gone */ }
      stripeCardRef.current = null;
      setPayReady(false);
    };
  }, [pendingIntent, pendingKind, theme]);

  /** Wallet path — Express Checkout; checkout/reserve only on confirm. */
  useEffect(() => {
    if (pendingKind !== 'wallet') return undefined;
    let cancelled = false;
    let expressElement: StripeExpressCheckoutElement | null = null;
    (async () => {
      const stripe = await getStripe();
      if (cancelled) return;
      if (!stripe) {
        setError('Payments are not configured (missing Stripe publishable key)');
        setPendingKind(null);
        return;
      }
      const elements = stripe.elements({
        mode: 'payment',
        amount: Number(beat.price_cents),
        currency: 'usd',
        appearance: {
          theme: theme === 'dark' ? 'night' : 'stripe',
          variables: { colorPrimary: BLUE, borderRadius: '10px', fontFamily: "'Inter', system-ui, sans-serif" },
        },
      });
      const preferApple = paymentRef.current === 'apple_pay';
      const created = elements.create('expressCheckout', {
        buttonHeight: 48,
        paymentMethodOrder: preferApple ? ['apple_pay'] : ['google_pay'],
        paymentMethods: {
          applePay: preferApple ? 'always' : 'never',
          googlePay: preferApple ? 'never' : 'always',
          link: 'never',
          paypal: 'never',
          amazonPay: 'never',
          klarna: 'never',
        },
      });
      expressElement = created;
      created.on('ready', ({ availablePaymentMethods }) => {
        if (cancelled) return;
        const hasWallet = preferApple
          ? !!availablePaymentMethods?.applePay
          : !!availablePaymentMethods?.googlePay;
        if (!hasWallet) {
          setError(
            preferApple
              ? 'Apple Pay is not available on this device or browser. Use Card instead.'
              : 'Google Pay is not available on this device or browser. Use Card instead.',
          );
          setPendingKind(null);
          setExpressReady(false);
          return;
        }
        setExpressReady(true);
      });
      created.on('confirm', async () => {
        setBusy(true);
        setError(null);
        let createdOrder: CheckoutResult | null = null;
        try {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message || 'Payment failed');
            return;
          }
          const tender = (paymentRef.current === 'apple_pay' || paymentRef.current === 'google_pay')
            ? paymentRef.current
            : 'google_pay';
          const result = await runCheckout(tender);
          if (!result) {
            setError('Could not start checkout — please try again');
            return;
          }
          if (result.status === 'paid') {
            onPurchased(result);
            return;
          }
          if (!(result.status === 'pending' && result.clientSecret)) {
            setError('Unexpected checkout state — please retry');
            return;
          }
          createdOrder = result;
          setPendingIntent(result);
          pendingIntentRef.current = result;

          const { error: payError, paymentIntent: pi } = await stripe.confirmPayment({
            elements,
            clientSecret: result.clientSecret,
            confirmParams: { return_url: paymentReturnUrl(result.orderId) },
            redirect: 'if_required',
          });
          if (payError) {
            await api.cancelPendingOrder(result.orderId).catch(() => {});
            setPendingIntent(null);
            pendingIntentRef.current = null;
            setError(payError.message || 'Payment failed');
            return;
          }
          if (pi && (pi.status === 'succeeded' || pi.status === 'processing' || pi.status === 'requires_capture')) {
            await pollUntilPaid(result);
            return;
          }
          await pollUntilPaid(result);
        } catch (e: any) {
          if (createdOrder?.orderId) {
            await api.cancelPendingOrder(createdOrder.orderId).catch(() => {});
            setPendingIntent(null);
            pendingIntentRef.current = null;
          }
          setError(checkoutErrorMessage(e));
        } finally {
          setBusy(false);
        }
      });
      if (cancelled || !expressElementRef.current) return;
      created.mount(expressElementRef.current);
      stripeWalletRef.current = { stripe, elements, expressElement: created };
    })();
    return () => {
      cancelled = true;
      try { expressElement?.unmount(); } catch { /* already gone */ }
      stripeWalletRef.current = null;
      setExpressReady(false);
    };
  }, [pendingKind, theme, beat.price_cents, payment, paymentReturnUrl, pollUntilPaid, onPurchased]);

  const confirmStripePayment = async () => {
    const ctx = stripeCardRef.current;
    if (!ctx || !pendingIntent) return;
    setBusy(true);
    setError(null);
    try {
      const { error: payError, paymentIntent } = await ctx.stripe.confirmPayment({
        elements: ctx.elements,
        confirmParams: { return_url: paymentReturnUrl(pendingIntent.orderId) },
        redirect: 'if_required',
      });
      if (payError) { setError(payError.message || 'Payment failed'); return; }
      if (paymentIntent && (paymentIntent.status === 'succeeded'
        || paymentIntent.status === 'processing'
        || paymentIntent.status === 'requires_capture')) {
        await pollUntilPaid(pendingIntent);
        return;
      }
      await pollUntilPaid(pendingIntent);
    } catch (e: any) {
      setError(e?.message || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmMockPayment = async () => {
    if (!pendingIntent?.clientSecret) return;
    setBusy(true);
    try {
      const intentId = pendingIntent.clientSecret.split('_secret')[0];
      await api.devSimulatePspSuccess(intentId);
      await pollUntilPaid(pendingIntent);
    } catch (e: any) {
      setError(e?.message || 'Payment failed');
    } finally {
      setBusy(false);
    }
  };

  const expBase = 'flex:1;display:flex;align-items:center;justify-content:center;gap:7px;padding:13px;border-radius:13px;cursor:pointer;transition:all .12s;';
  const rb = 'display:flex;align-items:center;gap:12px;width:100%;padding:13px 15px;border-radius:13px;cursor:pointer;transition:all .12s;';
  const cb = 'width:100%;padding:15px;border:none;border-radius:13px;font-weight:600;font-size:15px;transition:all .15s;';

  return (
    <BsPortal>
    <div style={s('position:fixed;inset:0;z-index:10000;background:var(--bs-overlay);backdrop-filter:blur(6px);display:flex;align-items:flex-start;justify-content:center;padding:44px 20px;overflow:auto')}>
      <div style={s('width:100%;max-width:516px;background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:22px;padding:24px;animation:bs-rise .22s ease both;box-shadow:0 30px 80px var(--bs-shadow-strong)')}>
        <div style={s('display:flex;align-items:center;justify-content:space-between;margin-bottom:20px')}>
          <h3 style={s('margin:0;font-weight:700;font-size:21px;letter-spacing:-0.01em')}>
            {inPaymentStep ? 'Confirm payment' : 'Complete purchase'}
          </h3>
          <button onClick={handleClose} disabled={busy} style={s('width:32px;height:32px;border-radius:9px;background:var(--bs-chip);border:1px solid var(--bs-border-2);color:var(--bs-text-mid);font-size:14px;cursor:pointer')}>✕</button>
        </div>

        <div style={s('display:flex;align-items:center;gap:14px;padding:14px;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:14px;margin-bottom:20px')}>
          <div style={s('width:46px;height:46px;border-radius:11px;background:radial-gradient(34px 24px at 60% 20%, rgba(47,107,255,0.3), transparent),var(--bs-thumb);display:flex;align-items:center;justify-content:center;flex-shrink:0')}><DiscIcon /></div>
          <div style={s('flex:1')}>
            <div style={s('font-weight:600;font-size:17px')}>{beat.name}</div>
            <div style={s('font-size:12.5px;color:var(--bs-text-muted);margin-top:2px')}>{beat.bpm} BPM · {beat.musical_key}</div>
          </div>
          <div style={s('text-align:right')}>
            <div style={s('font-weight:700;font-size:17px;color:var(--bs-text)')}>{priceText}</div>
            <div style={s('font-size:11px;color:var(--bs-text-faint)')}>+5% record</div>
          </div>
        </div>

        {pendingIntent && api.isMockPayments(pendingIntent.clientSecret) ? (
          <div>
            <div style={s('padding:16px;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:13px;margin-bottom:18px')}>
              <div style={s('display:inline-block;padding:3px 8px;background:var(--bs-amber-bg);color:var(--bs-amber);font-size:10px;font-weight:700;letter-spacing:0.08em;border-radius:6px;margin-bottom:10px')}>DEV · MOCK PSP</div>
              <p style={s('margin:0;color:var(--bs-text-muted);font-size:13px;line-height:1.5')}>
                The beat is reserved for you. In production this step shows the secure card / Apple Pay / Google Pay sheet.
              </p>
            </div>
            <button onClick={confirmMockPayment} disabled={busy}
              style={s(cb + (busy ? 'background:var(--bs-disabled);color:var(--bs-text-faint);cursor:wait' : 'background:var(--bs-btn-bg);color:var(--bs-btn-text);cursor:pointer'))}>
              {busy ? 'Processing…' : 'Simulate successful payment'}
            </button>
            <button onClick={backToMethods} disabled={busy}
              style={s('width:100%;margin-top:10px;padding:12px;border-radius:12px;border:1px solid var(--bs-border-2);background:transparent;color:var(--bs-text-mid);cursor:pointer;font-weight:600')}>
              Back
            </button>
          </div>
        ) : pendingKind === 'wallet' ? (
          <div>
            <p style={s('margin:0 0 14px;color:var(--bs-text-muted);font-size:13px;line-height:1.5')}>
              Complete with {payment === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}. The beat is reserved only when you confirm payment.
            </p>
            {!expressReady && !error && (
              <div style={s('padding:24px;text-align:center;color:var(--bs-text-faint);font-size:13px;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:13px;margin-bottom:18px')}>
                Loading {payment === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}…
              </div>
            )}
            <div ref={expressElementRef} style={s('margin-bottom:14px')} />
            {busy && (
              <div style={s('text-align:center;color:var(--bs-text-muted);font-size:13px;margin-bottom:10px')}>Processing…</div>
            )}
            <button onClick={backToMethods} disabled={busy}
              style={s('width:100%;padding:12px;border-radius:12px;border:1px solid var(--bs-border-2);background:transparent;color:var(--bs-text-mid);cursor:pointer;font-weight:600')}>
              Back
            </button>
          </div>
        ) : pendingIntent && pendingKind === 'card' ? (
          <div>
            <p style={s('margin:0 0 14px;color:var(--bs-text-muted);font-size:13px;line-height:1.5')}>
              The beat is reserved for you while the payment processes (USD).
            </p>
            {!payReady && !error && (
              <div style={s('padding:24px;text-align:center;color:var(--bs-text-faint);font-size:13px;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:13px;margin-bottom:18px')}>
                Loading secure payment form…
              </div>
            )}
            <div ref={payElementRef} style={s('margin-bottom:18px')} />
            <button onClick={confirmStripePayment} disabled={busy || !payReady}
              style={s(cb + (busy || !payReady ? 'background:var(--bs-disabled);color:var(--bs-text-faint);cursor:wait' : 'background:var(--bs-btn-bg);color:var(--bs-btn-text);cursor:pointer'))}>
              {busy ? 'Processing…' : `Pay ${fmtCents(beat.price_cents)}`}
            </button>
            <button onClick={backToMethods} disabled={busy}
              style={s('width:100%;margin-top:10px;padding:12px;border-radius:12px;border:1px solid var(--bs-border-2);background:transparent;color:var(--bs-text-mid);cursor:pointer;font-weight:600')}>
              Back
            </button>
          </div>
        ) : (
          <div>
            <div style={s('display:flex;gap:9px;margin-bottom:10px')}>
              <button
                type="button"
                onClick={() => { setPayment('apple_pay'); setError(null); }}
                aria-label="Apple Pay"
                aria-pressed={payment === 'apple_pay'}
                disabled={busy}
                style={s(expBase +
                  (payment === 'apple_pay'
                    ? 'color:#fff;background:#000;border:1px solid #000;'
                    : 'color:var(--bs-text-hi);background:var(--bs-chip);border:1px solid var(--bs-border-3);') +
                  (busy ? 'opacity:0.6;cursor:wait;' : ''))}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 12.55c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.47.74-3.11.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.03-1.43 2.49-.37 6.17 1.03 8.19.68.99 1.49 2.1 2.56 2.06 1.03-.04 1.42-.66 2.66-.66 1.24 0 1.59.66 2.68.64 1.11-.02 1.81-1 2.49-1.99.78-1.14 1.1-2.25 1.12-2.31-.02-.01-2.14-.82-2.16-3.24zM15.1 6.4c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.53.61-.99 1.59-.86 2.52.91.07 1.84-.46 2.41-1.15z" /></svg>
                <span style={s('font-weight:600;font-size:14px')}>Pay</span>
              </button>
              <button
                type="button"
                onClick={() => { setPayment('google_pay'); setError(null); }}
                aria-label="Google Pay"
                aria-pressed={payment === 'google_pay'}
                disabled={busy}
                style={s(expBase +
                  (payment === 'google_pay'
                    ? 'color:#fff;background:#000;border:1px solid #000;'
                    : 'color:var(--bs-text-hi);background:var(--bs-chip);border:1px solid var(--bs-border-3);') +
                  (busy ? 'opacity:0.6;cursor:wait;' : ''))}
              >
                <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M21.35 12.2c0-.63-.06-1.25-.16-1.84H12v3.49h5.25a4.49 4.49 0 0 1-1.95 2.94v2.44h3.15c1.84-1.7 2.9-4.2 2.9-7.03z" /><path fill="#34A853" d="M12 21.5c2.63 0 4.84-.87 6.45-2.36l-3.15-2.44c-.87.59-1.99.94-3.3.94-2.54 0-4.69-1.71-5.46-4.02H3.28v2.52A9.5 9.5 0 0 0 12 21.5z" /><path fill="#FBBC05" d="M6.54 13.62A5.7 5.7 0 0 1 6.24 12c0-.56.1-1.11.3-1.62V7.86H3.28A9.5 9.5 0 0 0 2.5 12c0 1.53.37 2.98 1.02 4.26z" /><path fill="#EA4335" d="M12 6.36c1.43 0 2.72.49 3.73 1.46l2.8-2.8C16.83 3.4 14.63 2.5 12 2.5a9.5 9.5 0 0 0-8.72 5.36l3.26 2.52C7.31 8.07 9.46 6.36 12 6.36z" /></svg>
                <span style={s('font-weight:600;font-size:14px;' + (payment === 'google_pay' ? 'color:#fff' : 'color:var(--bs-text-hi)'))}>Pay</span>
              </button>
            </div>
            <div style={s('font-size:11.5px;color:var(--bs-text-faint);text-align:center;margin-bottom:16px')}>
              Select Apple Pay, Google Pay, or a method below — then Confirm &amp; pay
            </div>
            <div style={s('display:flex;align-items:center;gap:12px;margin-bottom:16px')}>
              <div style={s('flex:1;height:1px;background:var(--bs-border)')} />
              <span style={s('font-size:11px;color:var(--bs-text-faint)')}>or</span>
              <div style={s('flex:1;height:1px;background:var(--bs-border)')} />
            </div>

            <div style={s('font-size:11px;font-weight:600;letter-spacing:0.12em;color:var(--bs-text-label);margin-bottom:11px')}>PAYMENT METHOD</div>
            <div style={s('display:flex;flex-direction:column;gap:9px;margin-bottom:18px')}>
              {methods.map((m) => {
                const active = payment === m.id;
                return (
                  <button key={m.id} type="button" onClick={() => !m.disabled && setPayment(m.id)} disabled={m.disabled}
                    style={s(rb + (m.disabled ? 'opacity:0.45;cursor:not-allowed;' : '') +
                      (active ? 'background:rgba(47,107,255,0.08);border:1px solid rgba(47,107,255,0.5)'
                        : 'background:var(--bs-input);border:1px solid var(--bs-border)'))}>
                    <div style={{ ...s('width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0'), border: `2px solid ${active ? BLUE : 'var(--bs-border-3)'}` }}>
                      <div style={{ ...s('width:9px;height:9px;border-radius:50%'), background: active ? BLUE : 'transparent' }} />
                    </div>
                    <div style={s('flex:1;text-align:left')}>
                      <div style={s('font-weight:600;font-size:14px;color:var(--bs-text-hi)')}>{m.label}</div>
                      <div style={s('font-size:12px;color:var(--bs-text-muted);margin-top:1px')}>{m.sub}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button type="button" onClick={() => setAgreed(!agreed)}
              style={s('display:flex;gap:11px;align-items:flex-start;width:100%;text-align:left;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:12px;padding:14px;cursor:pointer;margin-bottom:18px')}>
              <div style={s('width:20px;height:20px;border-radius:6px;flex-shrink:0;display:flex;align-items:center;justify-content:center;' +
                (agreed ? 'background:#2F6BFF;border:1px solid #2F6BFF' : 'background:transparent;border:1.5px solid var(--bs-border-3)'))}>
                {agreed && <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5l3 3 6-7" /></svg>}
              </div>
              <span style={s('font-size:12.5px;color:var(--bs-text-muted);line-height:1.5')}>
                I agree to Nova's standard <span style={s('color:var(--bs-text-body)')}>Beat License Terms &amp; Conditions{license ? ` (v${license.version})` : ''}</span> — exclusive
                transfer of master, {fmtCents(beat.price_cents)} (or {fmtPoints(beat.price_points)}) flat plus 5% of the record on all distribution, payable to the selling artist.
              </span>
            </button>

            <button type="button" onClick={onConfirm} disabled={!agreed || busy || !license}
              style={s(cb + (agreed && !busy && license ? 'background:var(--bs-btn-bg);color:var(--bs-btn-text);cursor:pointer' : 'background:var(--bs-disabled);color:var(--bs-text-faint);cursor:not-allowed'))}>
              {busy ? 'Processing…' : confirmLabel}
            </button>
            <div style={s('text-align:center;margin-top:11px;font-size:11.5px;color:var(--bs-text-faint)')}>Charged instantly · beat added to your account</div>
          </div>
        )}

        {error && <div style={s('margin-top:14px;padding:11px 13px;background:var(--bs-red-bg);border:1px solid var(--bs-red-border);border-radius:10px;color:var(--bs-red);font-size:13px')}>{error}</div>}
      </div>
    </div>
    </BsPortal>
  );
};
